import { useEffect, useRef, useState } from 'react';

import { digitosDoCep, ehUf, formatarCep } from '@/lib/localidades';
import { pedir } from '@/lib/pedido';

/** O que a busca preenche. O NUMERO nunca: o provedor nao sabe qual e, e preencher seria inventar. */
export interface EnderecoDoCep {
  readonly cep: string;
  readonly logradouro?: string;
  readonly bairro?: string;
  readonly uf?: string;
  readonly municipio?: string;
}

type Situacao =
  | { readonly tipo: 'quieto' }
  | { readonly tipo: 'buscando' }
  | { readonly tipo: 'achou' }
  | { readonly tipo: 'aviso'; readonly texto: string };

/**
 * O CEP que BUSCA SOZINHO: completou os oito numeros — digitando ou colando —,
 * o endereco vem (pedido do dono, 04/10/2026: "digitar o CEP e trazer
 * preenchido"). Antes havia um botao "Buscar" ao lado, e o CEP digitado sem o
 * clique nao trazia nada.
 *
 * CONTINUA OPCIONAL, em tres sentidos:
 *  - o cadastro nao depende dele: sem CEP, o endereco se digita a mao;
 *  - o provedor mora no ambiente da API (`CEP_ENDPOINT`), nunca no codigo — o
 *    portao `H12` proibe nome de maquina em `src/`. Sem ele, a rota responde
 *    501 e o campo fica quieto: recurso ausente nao e sistema quebrado;
 *  - o que ele preenche e SUGESTAO (decisao 12): quem cadastra confere e grava,
 *    e a tela marca o preenchido ate a pessoa mexer nele.
 *
 * A busca acontece UMA vez por CEP: voltar ao mesmo CEP nao refaz o pedido, e
 * nao passa por cima do que a pessoa corrigiu depois.
 */
export function CampoDeCep({
  id,
  valor,
  aoMudar,
  aoSair,
  aoPreencher,
  ...resto
}: {
  readonly id: string;
  readonly valor: string;
  readonly aoMudar: (valor: string) => void;
  readonly aoSair: (valor: string) => void;
  readonly aoPreencher: (achado: EnderecoDoCep) => void;
  readonly 'aria-invalid'?: boolean;
  readonly 'aria-describedby'?: string;
}) {
  const [situacao, setSituacao] = useState<Situacao>({ tipo: 'quieto' });
  const digitos = digitosDoCep(valor);
  /* O CEP que ja foi buscado, ou que ja veio gravado: abrir a edicao de um
     cadastro com CEP nao dispara busca nenhuma. */
  const jaBuscado = useRef<string | null>(digitos);
  const preencher = useRef(aoPreencher);
  useEffect(() => {
    preencher.current = aoPreencher;
  });

  useEffect(() => {
    if (digitos === null || digitos === jaBuscado.current) return undefined;
    jaBuscado.current = digitos;
    let vivo = true;
    setSituacao({ tipo: 'buscando' });
    void (async () => {
      try {
        const achado = await pedir<{ logradouro: string; bairro: string; municipio: string; uf: string }>(
          `/localidades/cep/${digitos}`,
        );
        if (!vivo) return;
        preencher.current({
          cep: digitos,
          ...(achado.logradouro ? { logradouro: achado.logradouro } : {}),
          ...(achado.bairro ? { bairro: achado.bairro } : {}),
          ...(ehUf(achado.uf) ? { uf: achado.uf, municipio: achado.municipio } : {}),
        });
        setSituacao({ tipo: 'achou' });
      } catch (erro) {
        if (!vivo) return;
        const status = (erro as { status?: number } | null)?.status;
        setSituacao(
          status === 501
            ? { tipo: 'quieto' }
            : status === 404
              ? { tipo: 'aviso', texto: 'CEP não encontrado. Confira os oito números, ou digite o endereço à mão.' }
              : { tipo: 'aviso', texto: 'Não foi possível consultar o CEP agora. Digite o endereço à mão; o cadastro não depende desta busca.' },
        );
      }
    })();
    return () => {
      vivo = false;
    };
  }, [digitos]);

  return (
    <div className="flex flex-col gap-1">
      <input
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete="postal-code"
        placeholder="30130-010"
        value={formatarCep(valor)}
        maxLength={9}
        className="controle w-full"
        onChange={(evento) => {
          aoMudar(evento.target.value.replace(/\D/g, '').slice(0, 8));
          if (situacao.tipo === 'aviso') setSituacao({ tipo: 'quieto' });
        }}
        onBlur={(evento) => {
          aoSair(evento.target.value);
        }}
        {...resto}
      />
      {/* Uma linha so, que fala com o leitor de tela sem roubar o foco. */}
      <p role="status" className={`txt-apoio ${situacao.tipo === 'aviso' ? 'text-parada-texto' : 'text-tinta-secundaria'}`}>
        {situacao.tipo === 'buscando'
          ? 'Buscando o endereço do CEP…'
          : situacao.tipo === 'achou'
            ? 'Endereço preenchido pelo CEP. Confira e escreva o número.'
            : situacao.tipo === 'aviso'
              ? situacao.texto
              : null}
      </p>
    </div>
  );
}
